import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { getPlatformDisplayInfo, type PlatformAlias } from '@mybonus/public';
import { APIInput, useAPI, useMutation, useQuery } from '@mybonus/ui';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { DateTime, StatusSnackbar, ErrorPopup, Page } from '../../components';

type ActivateConfigInput = APIInput['admin']['mychips']['config']['activate'];
type DeactivateConfigInput = APIInput['admin']['mychips']['config']['deactivate'];
type SetPreviewConfigInput = APIInput['admin']['mychips']['config']['setPreview'];
type ClearPreviewConfigInput = APIInput['admin']['mychips']['config']['clearPreview'];
type PreviewUserOption = {
	id: number;
	fullname: string | null;
	email: string;
	link: string;
	active: boolean;
};
type ConfigRow = {
	id: number;
	platform_id: number;
	platform_alias: PlatformAlias;
	integration_mode: 'IFRAME' | 'SDK';
	adunit_id: string;
	created_by_email: string | null;
	created_at: Date;
	updated_at: Date;
	is_active: boolean;
	is_preview: boolean;
	preview_revision_id: number | null;
	preview_user_id: number | null;
	preview_user_email: string | null;
	preview_user_name: string | null;
};
type PreviewDialogState = {
	row: ConfigRow;
	searchTerm: string;
	selectedUser: PreviewUserOption | null;
};
type MychipsConfigLocationState = {
	successText?: string;
};

function buildPreviewUserLabel(user: PreviewUserOption | null): string {
	if (!user) {
		return '';
	}

	return [user.fullname, user.email, user.link ? `#${user.link}` : null]
		.filter(Boolean)
		.join(' - ');
}

export function MychipsConfigView() {
	const { api } = useAPI();
	const location = useLocation();
	const navigate = useNavigate();
	const [successText, setSuccessText] = useState<string | null>(null);
	const [previewDialog, setPreviewDialog] = useState<PreviewDialogState | null>(null);

	const list = useQuery(
		'admin.mychips.config.list',
		(args) => api.admin.mychips.config.list.query(args),
		undefined,
		{ ttl: false },
	);

	const activate = useMutation<ActivateConfigInput>(
		(input) => api.admin.mychips.config.activate.mutate(input),
		{
			onSuccess: () => {
				setSuccessText('MyChips config revision activated');
				list.load({ refetch: true, bypassCache: true });
			},
		},
	);

	const deactivate = useMutation<DeactivateConfigInput>(
		(input) => api.admin.mychips.config.deactivate.mutate(input),
		{
			onSuccess: () => {
				setSuccessText('MyChips config deactivated');
				list.load({ refetch: true, bypassCache: true });
			},
		},
	);

	const setPreview = useMutation<SetPreviewConfigInput>(
		(input) => api.admin.mychips.config.setPreview.mutate(input),
		{
			onSuccess: () => {
				setSuccessText('MyChips preview updated');
				setPreviewDialog(null);
				list.load({ refetch: true, bypassCache: true });
			},
		},
	);

	const clearPreview = useMutation<ClearPreviewConfigInput>(
		(input) => api.admin.mychips.config.clearPreview.mutate(input),
		{
			onSuccess: () => {
				setSuccessText('MyChips preview cleared');
				list.load({ refetch: true, bypassCache: true });
			},
		},
	);

	const items = (list.data?.items || []) as unknown as ConfigRow[];
	const previewUserSearchInput = useMemo(
		() => ({
			pagination: { offset: 0, limit: 20 },
			freetext: previewDialog?.searchTerm.trim() || undefined,
		}),
		[previewDialog?.searchTerm],
	);
	const previewUsers = useQuery(
		'admin.user.list.preview',
		(args) =>
			previewDialog
				? api.admin.user.list.query(args)
				: Promise.resolve({ items: [], pagination: { totalRecords: 0, pages: 0, page: 1 } }),
		previewUserSearchInput,
		{ ttl: false },
	);
	const previewUserOptions = (
		(previewUsers.data?.items || []) as unknown as PreviewUserOption[]
	).filter((user) => user.active);
	useEffect(() => {
		const state = location.state as MychipsConfigLocationState | null;

		if (!state?.successText) {
			return;
		}

		setSuccessText(state.successText);
		navigate(location.pathname, { replace: true });
	}, [location.pathname, location.state, navigate]);

	function openPreviewDialog(row: ConfigRow) {
		setPreviewDialog({
			row,
			searchTerm: row.preview_user_email || row.preview_user_name || '',
			selectedUser: row.preview_user_id
				? {
						id: row.preview_user_id,
						fullname: row.preview_user_name,
						email: row.preview_user_email || '',
						link: '',
						active: true,
				  }
				: null,
		});
	}

	function handleSetPreview() {
		if (!previewDialog?.selectedUser) {
			return;
		}

		setPreview.mutate({
			platformId: previewDialog.row.platform_id,
			revisionId: previewDialog.row.id,
			userId: previewDialog.selectedUser.id,
		});
	}

	async function handleCopyCallbackUrl(type: string, url: string) {
		await navigator.clipboard.writeText(url);
		setSuccessText(`Copied ${type} callback URL`);
	}

	function formatCallbackTypeLabel(type: string): string {
		return `${type.charAt(0).toUpperCase()}${type.slice(1)}`;
	}

	const error =
		list.error || activate.error || deactivate.error || setPreview.error || clearPreview.error;
	const isLoading =
		list.isLoading ||
		activate.isLoading ||
		deactivate.isLoading ||
		setPreview.isLoading ||
		clearPreview.isLoading;

	return (
		<Page title="MyChips Config">
			<ErrorPopup
				error={error}
				onRetry={list.load}
				onClose={() => {
					list.reset();
					activate.reset();
					deactivate.reset();
					setPreview.reset();
					clearPreview.reset();
				}}
			/>

			<StatusSnackbar
				open={!!successText}
				onClose={() => setSuccessText(null)}
				text={successText || ''}
			/>

			{isLoading && <LinearProgress />}

			<Stack spacing={3}>
				<Paper sx={{ p: 3 }}>
					<Stack spacing={2}>
						<Typography variant="h6">Dashboard callback setup</Typography>
						<Typography variant="body2">
							Configure the main standard postback plus the rejected and deduction additional
							postbacks to point to the generated URLs below.
						</Typography>

						{list.data?.callbackUrls &&
							Object.entries(list.data.callbackUrls).map(([type, url]) => {
								const callbackTypeLabel = formatCallbackTypeLabel(type);

								return (
									<TextField
										key={type}
										size="small"
										label={`${callbackTypeLabel} callback URL`}
										value={url}
										InputProps={{
											readOnly: true,
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														size="small"
														title={`Copy ${callbackTypeLabel} callback URL`}
														aria-label={`Copy ${callbackTypeLabel} callback URL`}
														onClick={() => handleCopyCallbackUrl(type, url)}
													>
														<ContentCopyIcon fontSize="small" />
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								);
							})}
					</Stack>
				</Paper>

				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Stack spacing={0.5}>
						<Typography variant="h6">Configuration revisions</Typography>
						<Typography variant="body2">
							Review historical revisions, preview candidates, and manage public activation.
						</Typography>
					</Stack>
					<Button component={Link} to="/app/core/mychips-config/create" variant="contained">
						New revision
					</Button>
				</Stack>

				<TableContainer component={Paper} sx={{ width: '100%' }}>
					<Table sx={{ minWidth: 1250 }}>
						<TableHead>
							<TableRow>
								<TableCell>ID</TableCell>
								<TableCell>Platform</TableCell>
								<TableCell>Mode</TableCell>
								<TableCell>Ad unit</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Preview user</TableCell>
								<TableCell>Created by</TableCell>
								<TableCell>Created</TableCell>
								<TableCell>Updated</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{items.map((row: ConfigRow) => {
								const platformDisplay = getPlatformDisplayInfo(row.platform_alias);

								return (
									<TableRow
										key={row.id}
										sx={{ opacity: row.is_active || row.is_preview ? 1 : 0.7 }}
									>
										<TableCell>{row.id}</TableCell>
										<TableCell>
											<Tooltip title={platformDisplay.longLabel}>
												<Chip size="small" variant="outlined" label={platformDisplay.shortLabel} />
											</Tooltip>
										</TableCell>
										<TableCell>
											<Chip size="small" variant="outlined" label={row.integration_mode} />
										</TableCell>
										<TableCell>{row.adunit_id}</TableCell>
										<TableCell>
											<Stack direction="row" spacing={1}>
												{row.is_active && <Chip size="small" color="success" label="Active" />}
												{row.is_preview && <Chip size="small" color="info" label="Preview" />}
												{!row.is_active && !row.is_preview && (
													<Typography variant="body2">Inactive</Typography>
												)}
											</Stack>
										</TableCell>
										<TableCell>
											{row.is_preview ? (
												<Stack direction="row" spacing={0.5} alignItems="center">
													<Typography variant="body2">
														{row.preview_user_name ||
															row.preview_user_email ||
															`User #${row.preview_user_id}`}
													</Typography>
													<Tooltip title="Change preview user">
														<IconButton size="small" onClick={() => openPreviewDialog(row)}>
															<EditIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title="Clear preview">
														<span>
															<IconButton
																size="small"
																disabled={clearPreview.isLoading}
																onClick={() => clearPreview.mutate({ platformId: row.platform_id })}
															>
																<CloseIcon fontSize="small" />
															</IconButton>
														</span>
													</Tooltip>
												</Stack>
											) : (
												<Button
													size="small"
													variant="outlined"
													disabled={setPreview.isLoading || clearPreview.isLoading}
													onClick={() => openPreviewDialog(row)}
												>
													Set preview
												</Button>
											)}
										</TableCell>
										<TableCell>{row.created_by_email || '-'}</TableCell>
										<TableCell>
											<DateTime date={row.created_at} />
										</TableCell>
										<TableCell>
											<DateTime date={row.updated_at} />
										</TableCell>
										<TableCell>
											<Stack
												direction="row"
												spacing={0.5}
												sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 0.5 }}
											>
												<Button
													size="small"
													variant="outlined"
													disabled={activate.isLoading || deactivate.isLoading}
													onClick={() =>
														row.is_active
															? deactivate.mutate({ platformId: row.platform_id })
															: activate.mutate({
																	platformId: row.platform_id,
																	revisionId: row.id,
															  })
													}
												>
													{row.is_active ? 'Deactivate' : 'Activate'}
												</Button>
											</Stack>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>

				<Dialog
					open={!!previewDialog}
					onClose={() => !setPreview.isLoading && setPreviewDialog(null)}
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle>Set preview user</DialogTitle>
					<DialogContent>
						<Stack spacing={2} sx={{ pt: 1 }}>
							<Typography variant="body2">
								Only the selected user receives this revision before public activation.
							</Typography>
							<Typography variant="body2">
								Revision #{previewDialog?.row.id} on {previewDialog?.row.platform_alias}
							</Typography>
							<Autocomplete
								options={previewUserOptions}
								loading={previewUsers.isLoading}
								value={previewDialog?.selectedUser || null}
								inputValue={previewDialog?.searchTerm || ''}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								getOptionLabel={(option) => buildPreviewUserLabel(option)}
								onInputChange={(_event, value) =>
									setPreviewDialog((current) =>
										current
											? {
													...current,
													searchTerm: value,
											  }
											: current,
									)
								}
								onChange={(_event, value) =>
									setPreviewDialog((current) =>
										current
											? {
													...current,
													searchTerm: buildPreviewUserLabel(value),
													selectedUser: value,
											  }
											: current,
									)
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Preview user"
										helperText="Search by user id, email, name, or provider link"
									/>
								)}
							/>
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setPreviewDialog(null)} disabled={setPreview.isLoading}>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={handleSetPreview}
							disabled={!previewDialog?.selectedUser || setPreview.isLoading}
						>
							Save preview
						</Button>
					</DialogActions>
				</Dialog>
			</Stack>
		</Page>
	);
}
